import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { BookingTemplateTypes } from "@assets/types";
import AuthService from "@services/AuthService";
import type { RootState } from "@state/store";

export interface CompanyState {
  partners: BookingTemplateTypes.Partner[];
  isLoading: boolean;
}

export const initialCompanyState: CompanyState = {
  partners: [],
  isLoading: false,
};

export const getPartners = createAsyncThunk(
  "company/partners",
  (companyCode: string) =>
    AuthService.getPartners(companyCode, ["000", "SHP", "CEE"])
);

export const getDeliveryPartners = createAsyncThunk(
  "company/deliveryPartners",
  (companyCode: string) =>
    AuthService.getCompanyAddresses(companyCode, ["000,DEL"])
);

export const getNotifiers = createAsyncThunk(
  "company/notifiers",
  async (companyCode: string) => {
    //The Sea freight constructs the notifiers by concatenating the partners and the company's addresses
    const partners = await AuthService.getPartners(companyCode, [
      "000",
      "SHP",
      "CEE",
    ]);
    const notifierAddresses = await AuthService.getCompanyAddresses(
      companyCode,
      ["000", "NOT"]
    );
    return [...partners, ...notifierAddresses];
  }
);

export const companySlice = createSlice({
  name: "company",
  initialState: initialCompanyState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        getPartners.fulfilled,
        (state, action: PayloadAction<BookingTemplateTypes.Partner[]>) => {
          state.isLoading = false;
          state.partners = action.payload;
        }
      )
      .addCase(getPartners.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPartners.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(
        getNotifiers.fulfilled,
        (state, action: PayloadAction<BookingTemplateTypes.Partner[]>) => {
          state.isLoading = false;
          state.partners = action.payload;
        }
      )
      .addCase(getNotifiers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getNotifiers.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(
        getDeliveryPartners.fulfilled,
        (state, action: PayloadAction<BookingTemplateTypes.Partner[]>) => {
          state.isLoading = false;
          state.partners = action.payload;
        }
      )
      .addCase(getDeliveryPartners.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getDeliveryPartners.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const partnersState = (state: RootState) => state.companySlice;

export default companySlice.reducer;
